import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { clearStringValue } from 'Core/data/Helpers';
import { LogManagerConsole } from 'Core/logManager';

export const driverAdapter = createEntityAdapter({
    selectId: (driver) => String(driver.id)
});

const driverSlice = createSlice({
    name: 'realtime_driver',
    initialState: driverAdapter.getInitialState(),
    reducers: {
        upsertManyWithRules: (state, action) => {
            const items = action.payload;

            LogManagerConsole("RT_DEBUG", "[DriverSlice] upsertManyWithRules dispatched incoming size:", items ? items.length : 0);

            if (!items || !items.length) return;

            const validUpdates = [];
            const deletedIds = [];

            for (const newItem of items) {
                const driverId = String(newItem.id || newItem.driverId || newItem.entityId);
                if (!driverId || driverId === 'undefined') continue;

                if (newItem.event === 'deleted' || newItem._deleted) {
                    deletedIds.push(driverId);
                    continue;
                }

                const existingItem = state.entities[driverId] || {};

                // Let cross-slice tags resolution be handled via Thunk/Listener or Selector
                let tags = newItem.tags && Array.isArray(newItem.tags) ? newItem.tags : existingItem.tags || [];
                let tagIds = newItem.tagIds !== undefined ? newItem.tagIds : existingItem.tagIds;

                const avatarFile = newItem.avatarFile ? {
                    id: newItem.avatarFile.id,
                    repositoryId: newItem.avatarFile.repositoryId,
                    repositoryType: newItem.avatarFile.repositoryType,
                    repositoryCode: newItem.avatarFile.repositoryCode,
                    repositoryPath: newItem.avatarFile.repositoryPath,
                    fileKey: newItem.avatarFile.fileKey,
                    originalName: newItem.avatarFile.originalName
                } : existingItem.avatarFile || null;

                const objItem = {
                    ...existingItem,
                    id: driverId,
                    driverId: driverId,
                    name: newItem?.title || newItem?.name || existingItem.name,
                    status: newItem.status !== undefined ? newItem.status : existingItem.status,
                    avatarFileId: newItem.avatarFileId !== undefined ? newItem.avatarFileId : existingItem.avatarFileId,
                    avatarFile,
                    tempAvatarUrl: newItem.tempAvatarUrl !== undefined ? newItem.tempAvatarUrl : existingItem.tempAvatarUrl,
                    tagIds,
                    tags,
                    externalId: newItem?.externalId !== undefined ? newItem?.externalId : existingItem?.externalId,
                    driver_tz_id: newItem.driver_tz_id ? newItem.driver_tz_id : existingItem.idTimeZone,
                    supervisorId: newItem.supervisorId !== undefined ? newItem.supervisorId : existingItem.supervisorId,
                    licenseNumber: newItem?.licenseNumber !== undefined ? newItem?.licenseNumber : existingItem?.licenseNumber,
                    licenseCountry: newItem?.licenseCountry !== undefined ? newItem?.licenseCountry : existingItem?.licenseCountry,
                    licenseState: newItem?.licenseState !== undefined ? newItem?.licenseState : existingItem?.licenseState,
                    driverPin: newItem?.driverPin !== undefined ? newItem?.driverPin : existingItem?.driverPin,
                    hosRulesetIds: newItem?.hosRulesetIds !== undefined ? newItem?.hosRulesetIds : existingItem?.hosRulesetIds,
                    hosExempt: newItem?.hosExempt !== undefined ? newItem?.hosExempt : existingItem?.hosExempt,
                    hosExemptComment: newItem?.hosExemptComment !== undefined ? newItem?.hosExemptComment : existingItem?.hosExemptComment,
                    specialDutyStatuses: newItem?.specialDutyStatuses !== undefined ? newItem?.specialDutyStatuses : existingItem?.specialDutyStatuses,
                    email: newItem?.email !== undefined ? newItem?.email : existingItem?.email,
                    phone: undefined,
                    cell: newItem?.cell !== undefined ? newItem?.cell : existingItem?.cell,
                    notifyByEmail: newItem?.notifyByEmail !== undefined ? newItem?.notifyByEmail : existingItem?.notifyByEmail,
                    notifyBySms: newItem?.notifyBySms !== undefined ? newItem?.notifyBySms : existingItem?.notifyBySms,
                    updated_at: newItem.updated_at || existingItem.updated_at,
                    updatedBy: newItem.updatedBy || existingItem.updatedBy,
                    connected: newItem?.connected !== undefined ? newItem?.connected : existingItem?.connected,
                    lastConnected: newItem?.lastConnected !== undefined ? newItem?.lastConnected : existingItem?.lastConnected,
                    fsmDriver: newItem?.fsmDriver !== undefined ? newItem?.fsmDriver : existingItem?.fsmDriver,
                    fsmUsername: newItem?.fsmUsername !== undefined ? newItem?.fsmUsername : existingItem?.fsmUsername,
                    idTimeZone: newItem?.idTimeZone || newItem.driver_tz_id || existingItem.idTimeZone,
                    supervisor: newItem?.supervisor !== undefined ? newItem?.supervisor : existingItem?.supervisor,
                    unitId: newItem?.unitId !== undefined ? String(newItem?.unitId) : existingItem?.unitId,
                    unit: newItem?.unit !== undefined ? newItem?.unit : existingItem?.unit,
                    contactInfo: newItem?.contactInfo !== undefined ? newItem?.contactInfo : existingItem?.contactInfo,
                    keyfob: newItem?.keyfob !== undefined ? newItem?.keyfob : existingItem?.keyfob,
                    empNum: newItem?.empNum !== undefined ? newItem?.empNum : existingItem?.empNum,
                };

                validUpdates.push(objItem);
            }

            if (deletedIds.length > 0) {
                driverAdapter.removeMany(state, deletedIds);
            }
            if (validUpdates.length > 0) {
                driverAdapter.upsertMany(state, validUpdates);
            }
        },
        removeDrivers: driverAdapter.removeMany,
        clearDrivers: driverAdapter.removeAll,
    }
});

export const { upsertManyWithRules, removeDrivers, clearDrivers } = driverSlice.actions;

export const driverSelectors = driverAdapter.getSelectors(state => state.realtime.driver);

export default driverSlice.reducer;
